/**
 * SalesAllocationFormDialog — New / Edit / Copy for Sales Control › Sales Allocation.
 * Converted 1:1 from Access `frmCtrlSalesQrySaleAlcAdd` (table
 * `public.sales_allocation_scs`, PK `sales_allocation_scs_id`). Two-column Detail
 * (input Left 2496 → col 1, Left 9432 → col 2); rows by Top. Bold gutter section
 * headers split the body: General info · Outbound · Customer inbound · Allocation.
 *
 * New/Edit/Copy contract (from main form `frmCtrlSalesMainSubFormSaleAlc`):
 *  - new:  blank; sale_contract + consignment_contract enabled.
 *  - edit: set id; both contract combos DISABLED; hydrate from row. Split logic:
 *          sale_contract starts with 'S' → sale_contract set, consignment blank;
 *          starts with 'NPE' → consignment set, sale_contract blank; else both =
 *          the stored value. gdn_or_rdn 'RDN' → virtual_wh = receipt_no,
 *          receipt_no blank; 'GDN' → receipt_no set; else both. contract_quantity
 *          from DO snapshot. contract_no_of_bag = null.
 *  - copy: like edit BUT contract combos ENABLED and no id (new record);
 *          contract_no_of_bag = null.
 *
 * btnSave_Click maps: contract_no = sale_contract || consignment_contract;
 * receipt_no/gdn_or_rdn = receipt_no→GDN else virtual_wh→RDN. errCheck:
 *   1 neither contract, 2 both contracts, 3 both receipt+virtual_wh,
 *   4 seal_no already exists (New only). Cascades: contract→client (display),
 *   DO→contract_quantity/from_wh/to_wh, receipt/virtual_wh→export bags+qty
 *   (+allocating when terms CIF/FOB/FOT). All resolved through BE snapshot
 *   endpoints (see saleAllocationApi — TODO BE).
 */
import { useEffect, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type SaleAlcLookupItem,
  type SaleAllocationForm,
  type SaleAllocationWrite,
  useLazyGetSaleAllocationFormQuery,
  useCreateSaleAllocationMutation,
  useUpdateSaleAllocationMutation,
  useLazyCheckSaleAllocationSealQuery,
  useLazyGetSaleAllocationDoSnapshotQuery,
  useLazyGetSaleAllocationReceiptSnapshotQuery,
  useSaleAlcLookupSaleContractsQuery,
  useSaleAlcLookupConsignmentsQuery,
  useSaleAlcLookupDeliveryOrdersQuery,
  useSaleAlcLookupWarehousesQuery,
  useSaleAlcLookupReceiptNosQuery,
  useSaleAlcLookupVirtualWarehousesQuery,
} from '../../store/api/saleAllocationApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

type Mode = 'new' | 'edit' | 'copy'

interface Props {
  open: boolean
  mode: Mode
  /** PK `sales_allocation_scs_id` of the selected row (Edit/Copy). */
  saleAllocationId?: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  saleContract: string
  consignmentContract: string
  client: string            // readonly snapshot (.column(2) of contract)
  deliveryOrder: string
  contractNoOfBag: string   // readonly, always blank on open per main form
  contractQuantity: string  // readonly snapshot from DO
  mDate: string
  mTruckNo: string
  mNetWeight: string
  fromWarehouse: string
  toWarehouse: string
  containerNo: string
  sealNo: string
  noBag: string
  tareWeight: string
  tareCartonPallet: string
  tareCont: string
  maxGrossWeight: string
  receiptNo: string
  virtualWh: string
  exportNoOfBag: string     // readonly snapshot
  exportQuantity: string    // readonly snapshot
  netWeightAtBuyer: string
  deductionAtBuyerKg: string
  deductionAtBuyerVnd: string
  allocatingBag: string
  allocatingQuantity: string
  note: string
}

const EMPTY: Draft = {
  saleContract: '', consignmentContract: '', client: '', deliveryOrder: '',
  contractNoOfBag: '', contractQuantity: '', mDate: '', mTruckNo: '', mNetWeight: '',
  fromWarehouse: '', toWarehouse: '', containerNo: '', sealNo: '', noBag: '',
  tareWeight: '', tareCartonPallet: '', tareCont: '', maxGrossWeight: '',
  receiptNo: '', virtualWh: '', exportNoOfBag: '', exportQuantity: '',
  netWeightAtBuyer: '', deductionAtBuyerKg: '', deductionAtBuyerVnd: '',
  allocatingBag: '', allocatingQuantity: '', note: '',
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const sectionTitleSx: SxProps<Theme> = {
  fontWeight: 700, fontSize: '0.8125rem', color: 'primary.main',
  mt: 1.5, mb: 0.75, pb: 0.25, borderBottom: '1px solid', borderColor: 'divider',
}
const hintSx: SxProps<Theme> = { fontSize: '0.72rem', color: 'text.secondary', fontStyle: 'italic' }
const grid2Sx: SxProps<Theme> = {
  display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const readonlyFieldSx = {
  '& .MuiInputBase-root': { bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5') },
  '& .MuiInputBase-input': { color: (t: Theme) => t.palette.text.secondary, fontWeight: 600 },
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const str = (v: unknown): string => (v == null ? '' : String(v))

/** Inject the current value into options so the chip shows even before list load. */
function withCurrent(options: SaleAlcLookupItem[], value: string): SaleAlcLookupItem | null {
  if (!value) return null
  return options.find((o) => o.value === value) ?? { value, label: value }
}

function Combo(props: {
  label: string
  value: string
  options: SaleAlcLookupItem[]
  loading?: boolean
  disabled?: boolean
  required?: boolean
  error?: boolean
  helperText?: string
  onInputChange?: (q: string) => void
  onPick: (value: string, item: SaleAlcLookupItem | null) => void
}) {
  const { label, value, options, loading, disabled, required, error, helperText, onInputChange, onPick } = props
  const sel = withCurrent(options, value)
  const merged = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  // Dedupe by value — lookups can return duplicate codes (e.g. 'LDC'); a duplicate
  // value collapses to the same Autocomplete option key → React duplicate-key warning.
  const seen = new Set<string>()
  const opts = merged.filter((o) => {
    const k = String(o.value)
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
  return (
    <Autocomplete
      size={SM}
      fullWidth
      disabled={disabled}
      options={opts}
      value={sel}
      loading={loading}
      onChange={(_e, v) => onPick(v?.value ?? '', v)}
      onInputChange={onInputChange ? (_e, v, reason) => { if (reason === 'input') onInputChange(v) } : undefined}
      getOptionLabel={(o) => (!o ? '' : o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`)}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      filterOptions={onInputChange ? (x) => x : undefined}
      renderInput={(params) => (
        <TextField {...params} label={required ? `${label} *` : label} error={error} helperText={helperText} />
      )}
    />
  )
}

export default function SalesAllocationFormDialog({ open, mode, saleAllocationId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [sealExists, setSealExists] = useState(false)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  // Contract combos are disabled only in Edit (main form: edit sets Enabled=False;
  // copy keeps Enabled=True). Used too for the seal-no uniqueness gate (errCheck 4
  // only fires when sale_contract.Enabled = True, i.e. New/Copy).
  const contractsDisabled = mode === 'edit'

  const [loadForm] = useLazyGetSaleAllocationFormQuery()
  const [createRow, { isLoading: creating }] = useCreateSaleAllocationMutation()
  const [updateRow, { isLoading: updating }] = useUpdateSaleAllocationMutation()
  const [checkSeal] = useLazyCheckSaleAllocationSealQuery()
  const [fetchDoSnapshot] = useLazyGetSaleAllocationDoSnapshotQuery()
  const [fetchReceiptSnapshot] = useLazyGetSaleAllocationReceiptSnapshotQuery()
  const saving = creating || updating

  // Combo lookups — load while dialog open (server-side search via q).
  const skip = { skip: !open }
  const [scQ, setScQ] = useState('')
  const [cnQ, setCnQ] = useState('')
  const [doQ, setDoQ] = useState('')
  const saleContracts = useSaleAlcLookupSaleContractsQuery(scQ || undefined, skip)
  const consignments = useSaleAlcLookupConsignmentsQuery(cnQ || undefined, skip)
  const deliveryOrders = useSaleAlcLookupDeliveryOrdersQuery(doQ || undefined, skip)
  const warehouses = useSaleAlcLookupWarehousesQuery(undefined, skip)
  const receiptNos = useSaleAlcLookupReceiptNosQuery(undefined, skip)
  const virtualWhs = useSaleAlcLookupVirtualWarehousesQuery(undefined, skip)

  // ── Hydrate on open ───────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setSealExists(false)
    void (async () => {
      if (mode === 'new') {
        setDraft({ ...EMPTY, mDate: dayjs().format('YYYY-MM-DD') })
        return
      }
      if (!saleAllocationId) return
      const f = await loadForm(saleAllocationId).unwrap().catch(() => null)
      if (cancelled || !f) return
      const row = f as SaleAllocationForm
      // Contract split (main form): NPE* → consignment; else (S* or other) → sale_contract.
      // Access's else-branch fills BOTH, which trips the mutual-exclusion guard on the
      // web (contractBoth) — so route a single field instead.
      const stored = str(row.sale_contract)
      let saleContract = ''
      let consignmentContract = ''
      if (stored.slice(0, 3) === 'NPE') {
        consignmentContract = stored
      } else {
        saleContract = stored
      }
      // Receipt split (main form): RDN → virtual_wh; else (GDN/empty/unknown) → receipt_no.
      // Access's else-branch fills BOTH → trips receiptBoth on the web; route single.
      const gdnRdn = str(row.gdn_or_rdn).trim().toUpperCase()
      const receipt = str(row.receipt_no)
      let receiptNo = ''
      let virtualWh = ''
      if (gdnRdn === 'RDN') {
        virtualWh = receipt
      } else {
        receiptNo = receipt
      }
      // Access btnCopy does NOT carry seal_no / receipt_no / virtual_wh / export
      // (only btnEdit does) — leave them blank on Copy so a new record isn't created
      // with the source's seal (sealExists) or both receipt fields (receiptBoth).
      const isCopy = mode === 'copy'
      if (isCopy) { receiptNo = ''; virtualWh = '' }
      setDraft({
        ...EMPTY,
        saleContract,
        consignmentContract,
        client: str(row.client),
        deliveryOrder: str(row.delivery_order),
        contractNoOfBag: '', // main form forces null on open
        contractQuantity: fmtStd(row.contract_quantity),
        mDate: str(row.m_date).slice(0, 10),
        mTruckNo: str(row.m_truck_no),
        mNetWeight: fmtStd(row.m_net_weight),
        fromWarehouse: str(row.from_warehouse),
        toWarehouse: str(row.to_warehouse),
        containerNo: str(row.container_no),
        sealNo: isCopy ? '' : str(row.seal_no),
        noBag: fmtStd(row.number_bags),
        tareWeight: fmtStd(row.tare_weight),
        tareCartonPallet: fmtStd(row.tare_carton_pallet),
        tareCont: fmtStd(row.tare_cont),
        maxGrossWeight: fmtStd(row.max_gross_weight),
        receiptNo,
        virtualWh,
        exportNoOfBag: isCopy ? '' : fmtStd(row.export_no_of_bag),
        exportQuantity: isCopy ? '' : fmtStd(row.export_quantity),
        netWeightAtBuyer: fmtStd(row.net_weight_at_buyer),
        deductionAtBuyerKg: fmtStd(row.deduction_at_buyer_kg),
        deductionAtBuyerVnd: fmtStd(row.deduction_at_buyer_vnd),
        allocatingBag: fmtStd(row.allocating_bag),
        allocatingQuantity: fmtStd(row.allocating_quantity),
        note: str(row.note),
      })
    })()
    return () => { cancelled = true }
  }, [open, mode, saleAllocationId, loadForm])

  // ── seal_no uniqueness (seal_no_AfterUpdate + errCheck 4; New/Copy only) ──
  const checkSealNo = (v: string) => {
    const sealNo = v.trim()
    if (contractsDisabled || !sealNo) { setSealExists(false); return }
    void checkSeal({ sealNo, excludeId: saleAllocationId ?? undefined }).unwrap()
      .then((r) => setSealExists(r.exists)).catch(() => setSealExists(false))
  }

  // ── delivery_order_AfterUpdate: contract_quantity / from_wh / to_wh ──
  const handleDeliveryOrderPick = (doNo: string) => {
    setDraft((p) => ({ ...p, deliveryOrder: doNo, contractNoOfBag: '' }))
    if (!doNo) { set('contractQuantity', ''); return }
    void fetchDoSnapshot(doNo).unwrap()
      .then((r) => setDraft((p) => ({
        ...p,
        contractQuantity: fmtStd(r.contract_quantity),
        fromWarehouse: r.from_warehouse ?? p.fromWarehouse,
        toWarehouse: r.to_warehouse ?? p.toWarehouse,
      })))
      .catch(() => undefined)
  }

  // ── receipt_no / virtual_wh AfterUpdate: export bags+qty (+ allocating) ──
  const applyReceiptSnapshot = (kind: 'gdn' | 'rdn', value: string) => {
    if (!value) { set('exportNoOfBag', ''); set('exportQuantity', ''); return }
    void fetchReceiptSnapshot({ kind, value, saleContract: draft.saleContract || undefined }).unwrap()
      .then((r) => setDraft((p) => ({
        ...p,
        exportNoOfBag: fmtStd(r.export_no_of_bag),
        exportQuantity: fmtStd(r.export_quantity),
        // Only on New/Copy (Enabled=True) BE returns allocating values for CIF/FOB/FOT.
        allocatingBag: r.allocating_bag != null ? fmtStd(r.allocating_bag) : p.allocatingBag,
        allocatingQuantity: r.allocating_quantity != null ? fmtStd(r.allocating_quantity) : p.allocatingQuantity,
      })))
      .catch(() => undefined)
  }
  const handleReceiptPick = (v: string) => {
    // GDN side: picking receipt_no clears virtual_wh.
    setDraft((p) => ({ ...p, receiptNo: v, virtualWh: v ? '' : p.virtualWh }))
    applyReceiptSnapshot('gdn', v)
  }
  const handleVirtualWhPick = (v: string) => {
    // RDN side: picking virtual_wh clears receipt_no.
    setDraft((p) => ({ ...p, virtualWh: v, receiptNo: v ? '' : p.receiptNo }))
    applyReceiptSnapshot('rdn', v)
  }

  // ── btnSave errCheck 1/2/3 (+4 seal) gates ──
  const contractMissing = !draft.saleContract.trim() && !draft.consignmentContract.trim()
  const contractBoth = !!draft.saleContract.trim() && !!draft.consignmentContract.trim()
  const receiptBoth = !!draft.receiptNo.trim() && !!draft.virtualWh.trim()
  const canSave = canEdit && !contractMissing && !contractBoth && !receiptBoth && !sealExists && !saving

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (!canSave) return
    // contract_no = sale_contract || consignment_contract (btnSave_Click).
    const contractNo = draft.saleContract.trim() || draft.consignmentContract.trim() || null
    // receipt_no/gdn_or_rdn discriminator (btnSave_Click).
    let receiptNo: string | null = null
    let gdnOrRdn: string | null = null
    if (draft.receiptNo.trim()) { receiptNo = draft.receiptNo.trim(); gdnOrRdn = 'GDN' }
    else if (draft.virtualWh.trim()) { receiptNo = draft.virtualWh.trim(); gdnOrRdn = 'RDN' }
    const payload: SaleAllocationWrite = {
      sale_contract: contractNo,
      delivery_order: draft.deliveryOrder || null,
      m_date: draft.mDate || null,
      from_warehouse: draft.fromWarehouse || null,
      to_warehouse: draft.toWarehouse || null,
      m_truck_no: draft.mTruckNo || null,
      m_net_weight: parseNum(draft.mNetWeight),
      container_no: draft.containerNo || null,
      seal_no: draft.sealNo || null,
      number_bags: parseNum(draft.noBag),
      tare_weight: parseNum(draft.tareWeight),
      tare_carton_pallet: parseNum(draft.tareCartonPallet),
      tare_cont: parseNum(draft.tareCont),
      max_gross_weight: parseNum(draft.maxGrossWeight),
      receipt_no: receiptNo,
      gdn_or_rdn: gdnOrRdn,
      net_weight_at_buyer: parseNum(draft.netWeightAtBuyer),
      deduction_at_buyer_kg: parseNum(draft.deductionAtBuyerKg),
      deduction_at_buyer_vnd: parseNum(draft.deductionAtBuyerVnd),
      allocating_bag: parseNum(draft.allocatingBag),
      allocating_quantity: parseNum(draft.allocatingQuantity),
      note: draft.note || null,
    }
    try {
      if (mode === 'edit' && saleAllocationId) {
        await updateRow({ id: saleAllocationId, body: { ...payload, sales_allocation_scs_id: saleAllocationId } }).unwrap()
        snack.success('Sales Allocation updated')
      } else {
        await createRow(payload).unwrap()
        snack.success('Sales Allocation added')
      }
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Save sales allocation failed'))
    }
  }

  const title = mode === 'new' ? 'New' : mode === 'copy' ? 'Copy' : 'Edit'

  const dateField = (key: keyof Draft, label: string, gc: number | string, gr: number) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{ textField: { size: SM, fullWidth: true, InputLabelProps: { shrink: true }, sx: { gridColumn: { sm: gc }, gridRow: { sm: gr } } } }}
    />
  )
  const numField = (key: keyof Draft, label: string, gc: number | string, gr: number) => (
    <TextField
      label={label} size={SM} fullWidth type="text" value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      onBlur={() => set(key, fmtStd(draft[key] as string) as Draft[typeof key])}
      inputProps={numProps}
      sx={{ gridColumn: { sm: gc }, gridRow: { sm: gr } }}
    />
  )
  const textField = (key: keyof Draft, label: string, gc: number | string, gr: number, extra?: object) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      sx={{ gridColumn: { sm: gc }, gridRow: { sm: gr } }} {...extra} />
  )
  const roField = (key: keyof Draft, label: string, gc: number | string, gr: number, num?: boolean) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }}
      inputProps={num ? numProps : undefined}
      sx={{ gridColumn: { sm: gc }, gridRow: { sm: gr }, ...readonlyFieldSx }} />
  )

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>{title} — Sales Allocation</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit sales allocations.</Alert>}
        {contractMissing && <Alert severity="warning" sx={{ mb: 1.5 }}>Please choose an S contract or a Consignment.</Alert>}
        {contractBoth && <Alert severity="warning" sx={{ mb: 1.5 }}>Please choose only one: S contract or Consignment.</Alert>}
        {receiptBoth && <Alert severity="warning" sx={{ mb: 1.5 }}>Please choose only one: Receipt No or Virtual Warehouse.</Alert>}

        {/* ── Contracts (Top 456–1884) ── */}
        <Box sx={grid2Sx}>
          <Combo label="S Contract" value={draft.saleContract} disabled={contractsDisabled}
            options={saleContracts.data?.items ?? []} loading={saleContracts.isFetching} onInputChange={setScQ}
            error={contractBoth || contractMissing}
            onPick={(v, item) => setDraft((p) => ({ ...p, saleContract: v, consignmentContract: v ? '' : p.consignmentContract, client: v ? (item?.label ?? '') : '' }))} />
          <Combo label="Consignment" value={draft.consignmentContract} disabled={contractsDisabled}
            options={consignments.data?.items ?? []} loading={consignments.isFetching} onInputChange={setCnQ}
            error={contractBoth || contractMissing}
            onPick={(v, item) => setDraft((p) => ({ ...p, consignmentContract: v, saleContract: v ? '' : p.saleContract, client: v ? (item?.label ?? '') : '' }))} />
          <Typography sx={{ ...hintSx, gridColumn: { sm: '1 / span 2' } }}>* Only choose S contract or Consignment</Typography>
          {roField('client', 'Client', '1 / span 2', 3)}
          <Box sx={{ gridColumn: { sm: '1 / span 2' }, gridRow: { sm: 4 } }}>
            <Combo label="DO" value={draft.deliveryOrder}
              options={deliveryOrders.data?.items ?? []} loading={deliveryOrders.isFetching} onInputChange={setDoQ}
              onPick={(v) => handleDeliveryOrderPick(v)} />
          </Box>
          {roField('contractNoOfBag', 'No of bag', 1, 5, true)}
          {roField('contractQuantity', 'Quantity', 2, 5, true)}
        </Box>

        {/* ── General info (Access "Thông tin chung", Top 2292–3396) ── */}
        <Typography sx={sectionTitleSx}>General Information</Typography>
        <Box sx={grid2Sx}>
          {dateField('mDate', 'M. Date', 1, 1)}
          {textField('mTruckNo', 'M. Truck No', 2, 1)}
          <Box sx={{ gridColumn: { sm: 1 }, gridRow: { sm: 2 } }}>
            <Combo label="From WH" value={draft.fromWarehouse}
              options={warehouses.data?.items ?? []} loading={warehouses.isFetching}
              onPick={(v) => set('fromWarehouse', v)} />
          </Box>
          {numField('mNetWeight', 'M. Net weight', 2, 2)}
          <Box sx={{ gridColumn: { sm: '1 / span 2' }, gridRow: { sm: 3 } }}>
            <Combo label="To WH" value={draft.toWarehouse}
              options={warehouses.data?.items ?? []} loading={warehouses.isFetching}
              onPick={(v) => set('toWarehouse', v)} />
          </Box>
        </Box>

        {/* ── Outbound (Access "Xuất Kho", VGM block, Top 3756–6156) ── */}
        <Typography sx={sectionTitleSx}>Outbound</Typography>
        <Box sx={grid2Sx}>
          {textField('containerNo', 'Container No.', 1, 1)}
          {textField('sealNo', 'Seal No.', 2, 1, {
            error: sealExists,
            helperText: sealExists ? 'Seal No already exists' : undefined,
            onBlur: () => checkSealNo(draft.sealNo),
          })}
          {numField('noBag', 'No Bag.', 1, 2)}
          {numField('tareWeight', 'Tare Weight', 2, 2)}
          {numField('tareCartonPallet', 'Tare carton and pallet', 1, 3)}
          {numField('tareCont', 'Tare Cont', 2, 3)}
          {numField('maxGrossWeight', 'Max Gross Weight', '1 / span 2', 4)}
          <Box sx={{ gridColumn: { sm: 1 }, gridRow: { sm: 5 } }}>
            <Combo label="Receipt No" value={draft.receiptNo} error={receiptBoth}
              options={receiptNos.data?.items ?? []} loading={receiptNos.isFetching}
              onPick={(v) => handleReceiptPick(v)} />
          </Box>
          <Box sx={{ gridColumn: { sm: 2 }, gridRow: { sm: 5 } }}>
            <Combo label="Virtual Warehouse" value={draft.virtualWh} error={receiptBoth}
              options={virtualWhs.data?.items ?? []} loading={virtualWhs.isFetching}
              onPick={(v) => handleVirtualWhPick(v)} />
          </Box>
          <Typography sx={{ ...hintSx, gridColumn: { sm: '1 / span 2' }, gridRow: { sm: 6 } }}>* Only choose Receipt No or Virtual Warehouse</Typography>
          {roField('exportNoOfBag', 'No of bag', 1, 7, true)}
          {roField('exportQuantity', 'Quantity', 2, 7, true)}
        </Box>

        {/* ── Customer inbound (Access "Nhập Kho Khách Hàng", Top 6876–7608) ── */}
        <Typography sx={sectionTitleSx}>Customer Inbound</Typography>
        <Box sx={grid2Sx}>
          {numField('netWeightAtBuyer', 'Net weight at buyer', 1, 1)}
          {numField('deductionAtBuyerKg', 'Deduction Kg', 2, 1)}
          {numField('deductionAtBuyerVnd', 'Deduction Vnd', '1 / span 2', 2)}
        </Box>

        {/* ── Allocation (Access "Phân Bổ Số Lượng", Top 8016–8748) ── */}
        <Typography sx={sectionTitleSx}>Allocation</Typography>
        <Box sx={grid2Sx}>
          {numField('allocatingBag', 'Allocating Bag', 1, 1)}
          {numField('allocatingQuantity', 'Allocating Quantity', 2, 1)}
          {textField('note', 'Note', 1, 2, { multiline: true, minRows: 2, sx: { gridColumn: { sm: '1 / span 2' }, gridRow: { sm: 2 } } })}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
